import React from 'react'

import {
  connectorsForWallets,
  WalletDetailsParams,
} from '@rainbow-me/rainbowkit'
import {
  metaMaskWallet,
  rabbyWallet,
  coinbaseWallet,
  walletConnectWallet,
  ledgerWallet,
  safeWallet,
} from '@rainbow-me/rainbowkit/wallets'
import {
  // Chain,
  mainnet,
  base,
  polygon,
  sepolia,
  baseSepolia,
  polygonAmoy,
} from 'wagmi/chains'
import { Config, createConfig, http, WagmiProvider } from 'wagmi'
import {
  toPrivyWallet,
  toPrivyWalletConnector,
} from '@privy-io/cross-app-connect/rainbow-kit'

export const EMBEDDED_WALLET_ID = 'cmjidueid0007il0c7w6kijkn'

/**
 * Called to init wagmi.
 *
 * This code lives in a helper library and each parent app has a different way of injecting config hence
 * this function is called from the parent app.
 *
 * You MUST call this before <ConnectWalletContextProvider> is rendered.
 */
export const initWalletConfig = (
  alchemyAPIKey: string,
  walletconnectProjectId: string,
  useEmbedded: boolean = false
) => {
  const chains = [
    mainnet,
    polygon,
    base,
    sepolia,
    baseSepolia,
    polygonAmoy,
  ] as const

  const chainUrls = {
    [mainnet.id]: `https://eth-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [polygon.id]: `https://polygon-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [base.id]: `https://base-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [sepolia.id]: `https://eth-sepolia.g.alchemy.com/v2/${alchemyAPIKey}`,
    [baseSepolia.id]: `https://base-sepolia.g.alchemy.com/v2/${alchemyAPIKey}`,
    [polygonAmoy.id]: `https://polygon-amoy.g.alchemy.com/v2/${alchemyAPIKey}`,
  }

  const transports = {
    [mainnet.id]: http(chainUrls[mainnet.id]),
    [polygon.id]: http(chainUrls[polygon.id]),
    [base.id]: http(chainUrls[base.id]),
    [sepolia.id]: http(chainUrls[sepolia.id]),
    [baseSepolia.id]: http(chainUrls[baseSepolia.id]),
    [polygonAmoy.id]: http(chainUrls[polygonAmoy.id]),
  }

  const coinbaseWalletConnector = coinbaseWallet
  coinbaseWalletConnector.preference = 'eoaOnly'

  const embeddedWalletBase = toPrivyWallet({
    id: EMBEDDED_WALLET_ID,
    name: 'Verisart',
    iconUrl: 'https://my-wallet.verisart.com/images/v-logo-black.png',
  })

  const embeddedWallet = () => {
    return {
      ...embeddedWalletBase(),
      createConnector: (walletDetails: WalletDetailsParams) =>
        toPrivyWalletConnector(
          {
            id: EMBEDDED_WALLET_ID,
            name: 'Verisart',
            iconUrl: 'https://my-wallet.verisart.com/images/v-logo-black.png',
            smartWalletMode: false,
          },
          walletDetails
        ),
    }
  }

  const wallets = [
    metaMaskWallet,
    rabbyWallet,
    coinbaseWalletConnector,
    ledgerWallet,
    safeWallet,
    walletConnectWallet,
  ]

  const connectors = connectorsForWallets(
    [
      ...(useEmbedded
        ? [
            {
              groupName: 'Express wallet with email',
              wallets: [embeddedWallet],
            },
          ]
        : []),
      {
        groupName: 'Recommended',
        wallets,
      },
    ],
    {
      projectId: walletconnectProjectId,
      walletConnectParameters: {
        qrModalOptions: {
          themeVariables: {
            // Ensure the modal appears above our modal
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            '--w3m-z-index': '9999999',

            // So weirdly, the TS bindings have the new CSS variable name (see above)
            // but for some reason this wasn't working yet. We're setting the old one here too to be safe.
            '--wcm-z-index': '9999999',
          },
        },
      },
    }
  )

  return createConfig({
    connectors,
    multiInjectedProviderDiscovery: true,
    chains: chains,
    transports,
    ssr: true,
  })
}

const ConnectWalletContextProvider = ({
  children,
  config,
}: {
  children: React.ReactNode
  config: Config
}) => {
  return <WagmiProvider config={config}>{children}</WagmiProvider>
}

export default ConnectWalletContextProvider
